import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiEdit, FiTrash2, FiUser, FiMail, FiPhone, FiMapPin, FiCreditCard, FiCalendar, FiAtSign } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageHeader from "../../../components/common/pageheader/PageHeader";
import { deleteUser, getUserById, updateUser } from "../../../services/usersApi";

import ConfirmDeleteModal from "../../../components/common/deleteModal/ConfirmDeleteModal";
import UserFormModal from "../UserFormModal/UserFormModal";

import styles from "./UserDetailPage.module.css";
import Loader from "../../../components/common/Loader/Loader";

const stripPhonePrefix = (stored) => {
  if (!stored) return "";
  return stored.startsWith("+994") ? stored.slice(4) : stored;
};

const UserDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = Number(id);

  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [error, setError] = useState("");

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    id: null,
    name: "",
    email: "",
    role: "ADMIN",
    nationalId: "",
    phone: "",
    address: "",
  });
  const [saving, setSaving] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const fetchUser = async () => {
    if (Number.isNaN(userId)) {
      setError(t("pages.userDetail.invalidId"));
      return;
    }

    try {
      setLoadingUser(true);
      setError("");

      const data = await getUserById(userId);
      setUser(data);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.userDetail.loadError");

      setError(msg);
    } finally {
      setLoadingUser(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const goBack = () => {
    navigate("/users");
  };

  const openEditModal = () => {
    if (!user) return;

    setFormValues({
      id: user.id,
      name: user.name || "",
      email: user.email || "",
      role: user.role || "ADMIN",
      nationalId: user.nationalId || "",
      phone: stripPhonePrefix(user.phone),
      address: user.address || "",
    });

    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSaving(false);
    setError("");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (!formValues.name.trim()) {
        setError(t("pages.userDetail.nameRequired"));
        setSaving(false);
        return;
      }

      if (!formValues.email.trim()) {
        setError(t("pages.userDetail.emailRequired"));
        setSaving(false);
        return;
      }

      const phoneLocal = (formValues.phone || "").trim();

      const payload = {
        email: formValues.email.trim(),
        name: formValues.name.trim(),
        role: formValues.role,
        nationalId: formValues.nationalId?.trim() || "",
        phone: phoneLocal ? `+994${phoneLocal}` : null,
        address: formValues.address?.trim() || "",
      };

      await updateUser(formValues.id, payload);

      toast.success(t("pages.userDetail.updateSuccess"), {
        toastId: "user-detail-update-success",
      });

      closeEditModal();
      fetchUser();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.userDetail.operationFailed");

      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const cancelDeleteUser = () => {
    setDeleteModalOpen(false);
  };

  const confirmDeleteUser = async () => {
    if (!user) return;

    try {
      setLoadingUser(true);
      setError("");

      await deleteUser(user.id);

      toast.success(t("pages.userDetail.deleteSuccess"));

      setDeleteModalOpen(false);
      navigate("/users");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.userDetail.deleteFailed");

      setError(msg);
    } finally {
      setLoadingUser(false);
    }
  };

  const createdAt = user?.createdDateTime && new Date(user.createdDateTime).toLocaleString("az-Latn-AZ");
  const updatedAt = user?.updatedDateTime && new Date(user.updatedDateTime).toLocaleString("az-Latn-AZ");

  /* Bütün əvvəlki sahələr – boş olanlar "-" ilə göstərilir */
  const mainFields = [
    { icon: FiMail, label: t("pages.userDetail.email"), value: user?.email },
    { icon: FiPhone, label: t("pages.userDetail.phone"), value: user?.phone },
    { icon: FiMapPin, label: t("pages.userDetail.address"), value: user?.address },
    { icon: FiCreditCard, label: t("pages.userDetail.nationalId"), value: user?.nationalId },
    { icon: FiAtSign, label: t("pages.userDetail.username"), value: user?.username },
  ];

  const auditFields = [
    { icon: FiUser, label: t("pages.userDetail.createdBy"), value: user?.createdBy },
    { icon: FiUser, label: t("pages.userDetail.updatedBy"), value: user?.updatedBy },
    { icon: FiCalendar, label: t("pages.userDetail.createdAt"), value: createdAt },
    { icon: FiCalendar, label: t("pages.userDetail.updatedAt"), value: updatedAt },
  ];

  const displayName = user?.name || user?.email || t("pages.userDetail.noNameFallback");
  const initials = user?.name
    ? user.name.trim().split(/\s+/).map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?"
    : user?.email?.[0]?.toUpperCase() || "?";

  return (
    <div className={styles.container}>
      <PageHeader title={t("pages.userDetail.title")} subtitle={user ? displayName : undefined} />

      <div className={styles.topBar}>
        <button type="button" onClick={goBack} className={styles.backBtn}>
          <FiArrowLeft size={18} />
          <span>{t("common.back")}</span>
        </button>

        <div className={styles.rightActions}>
          <button type="button" onClick={openEditModal} className={styles.primaryBtn} disabled={!user}>
            <FiEdit size={17} />
            <span>{t("pages.userDetail.edit")}</span>
          </button>

          <button type="button" onClick={openDeleteModal} className={styles.dangerBtn} disabled={!user}>
            <FiTrash2 size={17} />
            <span>{t("pages.userDetail.delete")}</span>
          </button>
        </div>
      </div>

      {loadingUser && <Loader fullscreen />}
      {error && <p className={styles.error}>{t("common.errorLabel")}: {error}</p>}

      {!loadingUser && user && (
        <div className={styles.content}>
          <section className={styles.card}>
            <div className={styles.profileBlock}>
              <div className={styles.avatarWrap}>
                <span className={styles.avatarInitials}>{initials}</span>
              </div>
              <div className={styles.profileCardText}>
                <h2 className={styles.profileName}>{displayName}</h2>
                <p className={styles.profileMeta}>
                  {user.role && <span className={styles.roleBadge}>{t(`userRole.${user.role}`, { defaultValue: user.role })}</span>}
                </p>
              </div>
            </div>

            <h3 className={styles.sectionTitle}>
              <FiUser className={styles.sectionIcon} />
              {t("pages.userDetail.personalInfo")}
            </h3>
            <div className={styles.fieldGrid}>
              {mainFields.map(({ icon: Icon, label, value }) => (
                <div key={label} className={styles.field}>
                  <div className={styles.fieldIcon}>
                    <Icon size={18} />
                  </div>
                  <div className={styles.fieldContent}>
                    <span className={styles.label}>{label}</span>
                    <span className={styles.value}>{value ?? "-"}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className={styles.card}>
            <h3 className={styles.sectionTitle}>
              <FiCalendar className={styles.sectionIcon} />
              {t("pages.userDetail.auditInfo")}
            </h3>
            <div className={styles.fieldGrid}>
              {auditFields.map(({ icon: Icon, label, value }) => (
                <div key={label} className={styles.field}>
                  <div className={styles.fieldIcon}>
                    <Icon size={18} />
                  </div>
                  <div className={styles.fieldContent}>
                    <span className={styles.label}>{label}</span>
                    <span className={styles.value}>{value ?? "-"}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      <UserFormModal
        isOpen={isEditModalOpen}
        mode="edit"
        values={formValues}
        onChange={handleFormChange}
        onClose={closeEditModal}
        onSubmit={handleFormSubmit}
        saving={saving}
        error={error}
      />

      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        onConfirm={confirmDeleteUser}
        onCancel={cancelDeleteUser}
        message={user ? t("pages.userDetail.deleteConfirm", { name: user.name }) : ""}
        disabled={loadingUser}
      />
    </div>
  );
};

export default UserDetailPage;
